import { RotateCcw } from 'lucide-react';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import { dateFr } from '../utils/dates';

/**
 * Le décompte avant la remise à zéro d'une démonstration.
 *
 * POURQUOI IL REMPLACE « OFFERT — J-XXX » PLUTÔT QUE DE S'AJOUTER
 * ---------------------------------------------------------------------------
 * Les deux badges disent une échéance, au même endroit, dans la même forme.
 * Côte à côte, on lit le premier et l'on croit avoir lu les deux — et les deux
 * ne parlent pas de la même chose : l'un annonce la fin d'un cadeau, l'autre
 * l'effacement de ce qu'on vient de saisir.
 *
 * Sur une démonstration, c'est le second qui compte. La période offerte n'a
 * d'ailleurs aucun sens ici : personne ne paie une démonstration.
 *
 * IL S'ADRESSE AUX VISITEURS, ET NON AUX MEMBRES
 * ---------------------------------------------------------------------------
 * Contrairement au badge de gratuité, qui ne regarde que le bureau, celui-ci
 * se montre à tout le monde — c'est justement le visiteur de passage qui a
 * besoin de savoir que son essai sera effacé, et quand. Il n'est plus là pour
 * le lire si on le lui dit après.
 */
export default function BadgeReset({ className = '' }: { className?: string }) {
  const perso = usePersonnalisation();
  const demo = (perso as { demonstration?: { prochainReset: string; joursAvantReset: number } | null })
    ?.demonstration;

  if (demo === null || demo === undefined) {
    return null;
  }

  const jours = demo.joursAvantReset;
  const quand = dateFr(new Date(demo.prochainReset + 'T05:00:00'));

  return (
    <span
      /*
       * Le badge porte la couleur de SA démonstration.
       *
       * Il était bleu ciel partout : la même pastille sur les vingt et une,
       * alors que tout le reste de l'écran — les boutons, l'onglet actif, la
       * carte sur la vitrine — porte déjà la teinte de la discipline. Un seul
       * élément d'une autre couleur se lit comme une alerte, ce qu'il n'est
       * pas : c'est une information de fonctionnement.
       *
       * Par les tokens « primary » et non par une couleur en ligne : ils sont
       * déjà ceux de l'instance, servis avant le premier affichage, et déjà
       * corrigés pour rester lisibles — le jaune du volley y arrive assombri.
       * Une couleur en ligne obligerait à refaire ce calcul ici, dans le
       * navigateur, sur une valeur brute.
       */
      className={`inline-flex items-center gap-1.5 rounded-full bg-primary-100 text-primary-900 dark:bg-primary-900/40 dark:text-primary-100 px-2.5 py-0.5 text-[0.7rem] font-semibold whitespace-nowrap ${className}`}
      title={`Cette démonstration revient à son état de départ le ${quand}. Tout ce que vous saisissez d'ici là sera effacé — c'est fait pour : essayez sans retenue.`}
    >
      <RotateCcw className="w-3 h-3 shrink-0" aria-hidden="true" />
      {jours <= 1 ? 'Reset — demain' : `Reset — J-${jours}`}
    </span>
  );
}
